import React, { useMemo } from "react";
import Select from "react-select";

const compareNames = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, {
    sensitivity: "base",
  });

/**
 * Multi-select with search, options grouped by groupName (A–Z),
 * and items sorted A–Z within each group.
 *
 * Expects data items shaped like: { id, name, groupName? }
 * value / setValue use the same { id, name, ... } shape as MultipleDropDown.
 */
const GroupedSearchableMultiSelect = ({
  data = [],
  placeholder = "Select...",
  setValue,
  value = [],
}) => {
  const groupedOptions = useMemo(() => {
    const byGroup = new Map();

    (data || []).forEach((item) => {
      const groupName = item.groupName || "Ungrouped";
      if (!byGroup.has(groupName)) {
        byGroup.set(groupName, []);
      }
      byGroup.get(groupName).push({
        ...item,
        value: item.id,
        label: item.name,
      });
    });

    return Array.from(byGroup.entries())
      .sort(([a], [b]) => compareNames(a, b))
      .map(([groupName, options]) => ({
        label: groupName,
        options: options.sort((a, b) => compareNames(a.label, b.label)),
      }));
  }, [data]);

  const selectedOptions = useMemo(() => {
    const selectedIds = new Set((value || []).map((v) => v.id));
    return groupedOptions
      .flatMap((group) => group.options)
      .filter((option) => selectedIds.has(option.value));
  }, [groupedOptions, value]);

  const handleChange = (selected) => {
    if (!selected || selected.length === 0) {
      setValue([]);
      return;
    }

    setValue(
      selected.map((option) => ({
        id: option.value,
        name: option.label,
        ...(option.groupName != null ? { groupName: option.groupName } : {}),
      }))
    );
  };

  const formatGroupLabel = (group) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
      }}
    >
      <span>{group.label}</span>
      <span
        style={{
          fontSize: "0.7rem",
          fontWeight: 600,
          letterSpacing: 0,
          textTransform: "none",
          color: "rgba(0, 0, 0, 0.55)",
          background: "rgba(0, 0, 0, 0.08)",
          borderRadius: 999,
          padding: "1px 7px",
        }}
      >
        {group.options.length}
      </span>
    </div>
  );

  return (
    <div style={{ minWidth: 220, maxWidth: 360, margin: "8px 0" }}>
      <Select
        isMulti
        isSearchable
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        options={groupedOptions}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        formatGroupLabel={formatGroupLabel}
        classNamePrefix="grouped-multi-select"
        styles={{
          control: (base) => ({
            ...base,
            minHeight: 40,
            borderColor: "rgba(0, 0, 0, 0.23)",
            boxShadow: "none",
            "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)" },
          }),
          group: (base) => ({
            ...base,
            paddingTop: 0,
            paddingBottom: 6,
          }),
          groupHeading: (base) => ({
            ...base,
            position: "sticky",
            top: 0,
            zIndex: 2,
            margin: 0,
            padding: "8px 10px",
            fontWeight: 800,
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "#1a237e",
            backgroundColor: "#e8eaf6",
            borderBottom: "1px solid #c5cae9",
          }),
          option: (base) => ({
            ...base,
            paddingLeft: 18,
            fontSize: "0.9rem",
          }),
          multiValueLabel: (base) => ({
            ...base,
            fontSize: "0.85rem",
          }),
          menu: (base) => ({
            ...base,
            zIndex: 20,
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: 360,
            paddingTop: 0,
          }),
        }}
      />
    </div>
  );
};

export default GroupedSearchableMultiSelect;
